import { useQueryClient } from '@tanstack/react-query';
import {
  getStrategyTypeId,
  mapStrategyDetailApiToUi,
  mapStrategyListItemApiToUi,
  mapStrategyUiToCreateRequest,
  mapStrategyUiToUpdateRequest,
} from '@/lib/mappers/strategies';
import { mockActionTypeOptions, mockEmailTemplateOptions, mockStrategyTypeOptions } from '@/lib/demo-data/strategies';
import { generateMockStrategies } from '@/lib/mock-data/strategies-mock-data';
import type {
  CopyStrategyToGroupsApiResponse,
  CreateStrategyResponse,
  StrategiesApiResponse,
  StrategyActionTypeLookupApi,
  StrategyCopyTargetApi,
  StrategyDetailApi,
  StrategyTypeLookupApi,
  TemplateListItemApi,
  UpdateStrategyResponse,
} from '@/types/api/strategies';
import type { CollectionStrategyItem, StrategyAction } from '@/types/collection-strategies';
import {
  copyStrategyToGroupsDemoResponse,
  createStrategyDemoResponse,
  deleteStrategyDemoResponse,
  makeUpdateStrategyDemoFn,
  makeUpdateStrategyStatusDemoFn,
  runStrategyNowDemoResponse,
  strategyCopyTargetsDemoData,
  toggleStrategyStatusDemoResponse,
} from '@/services/demo/strategies-demo.service';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

export interface GetStrategiesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const mockStrategies = generateMockStrategies();

export function useStrategiesList(params?: GetStrategiesParams) {
  const { apiGet } = useApi();

  return useModeAwareQuery<CollectionStrategyItem[], Error>(
    ['strategies', params],
    async ({ signal }) => {
      const response = await apiGet<StrategiesApiResponse>('/api/v1/strategies', params, signal);
      return response.data.map(mapStrategyListItemApiToUi);
    },
    mockStrategies,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

export function useStrategyById(strategyId: number | string | null) {
  const { apiGet } = useApi();
  const mockStrategy = mockStrategies.find((s) => s.id === String(strategyId)) ?? null;

  return useModeAwareQuery<CollectionStrategyItem | null, Error>(
    ['strategy', strategyId],
    async ({ signal }) => {
      if (!strategyId) return null;
      const response = await apiGet<StrategyDetailApi>(`/api/v1/strategies/${strategyId}`, undefined, signal);
      return mapStrategyDetailApiToUi(response);
    },
    mockStrategy,
    {
      enabled: !!strategyId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

export function useCreateStrategy({ forceLive = false }: { forceLive?: boolean } = {}) {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CreateStrategyResponse, Error, Partial<CollectionStrategyItem>>(
    async (data) => {
      const strategyTypeId = data.type ? getStrategyTypeId(data.type) : 1;
      const request = mapStrategyUiToCreateRequest(data, strategyTypeId);
      return apiPost<CreateStrategyResponse>('/api/v1/strategies', request);
    },
    createStrategyDemoResponse,
    {
      forceLive,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['strategies'] });
      },
    },
  );
}

export function useUpdateStrategy(strategyId: number | string, { forceLive = false }: { forceLive?: boolean } = {}) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateStrategyResponse, Error, Partial<CollectionStrategyItem>>(
    async (data) => {
      const strategyTypeId = data.type ? getStrategyTypeId(data.type) : undefined;
      const request = mapStrategyUiToUpdateRequest(data, strategyTypeId);
      return apiPut<UpdateStrategyResponse>(`/api/v1/strategies/${strategyId}`, request);
    },
    makeUpdateStrategyDemoFn(strategyId),
    {
      forceLive,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['strategies'] });
        queryClient.invalidateQueries({ queryKey: ['strategy', strategyId] });
      },
    },
  );
}

export function useDeleteStrategy() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<{ message: string }, Error, number | string>(
    async (strategyId) => {
      return apiDelete<{ message: string }>(`/api/v1/strategies/${strategyId}`);
    },
    deleteStrategyDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['strategies'] });
        queryClient.invalidateQueries({ queryKey: ['strategies-graphql-list'] });
      },
    },
  );
}

export interface CopyStrategyToGroupsVariables {
  strategyId: number | string;
  companyGroupIds: number[];
}

/** Company groups the current user can copy a strategy into, excluding the current one. */
export function useStrategyCopyTargets(enabled: boolean) {
  const { apiGet } = useApi();

  return useModeAwareQuery<StrategyCopyTargetApi[], Error>(
    ['strategy-copy-targets'],
    async ({ signal }) => apiGet<StrategyCopyTargetApi[]>('/api/v1/strategies/copy-targets', undefined, signal),
    strategyCopyTargetsDemoData,
    { enabled, staleTime: 60 * 1000, refetchOnWindowFocus: false },
  );
}

export function useCopyStrategyToGroups() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CopyStrategyToGroupsApiResponse, Error, CopyStrategyToGroupsVariables>(
    async ({ strategyId, companyGroupIds }) =>
      apiPost<CopyStrategyToGroupsApiResponse>(`/api/v1/strategies/${strategyId}/copy-to-groups`, {
        companyGroupIds,
      }),
    copyStrategyToGroupsDemoResponse,
    {
      onSuccess: () => {
        // The copies land in other groups, but the current list is refreshed for consistency.
        queryClient.invalidateQueries({ queryKey: ['strategies'] });
        queryClient.invalidateQueries({ queryKey: ['strategies-graphql-list'] });
      },
    },
  );
}

export interface RunStrategyNowResponse {
  runId: number;
  strategyId: number;
  message: string;
}

export function useRunStrategyNow() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<RunStrategyNowResponse, Error, number | string>(
    async (strategyId) => {
      return apiPost<RunStrategyNowResponse>(`/api/v1/strategies/${strategyId}/run`);
    },
    runStrategyNowDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['strategies'] });
        queryClient.invalidateQueries({ queryKey: ['strategies-graphql-list'] });
      },
    },
  );
}

export function useUpdateStrategyStatus(strategyId: number | string) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateStrategyResponse, Error, { status: 'Active' | 'Inactive' }>(
    async (data) => {
      return apiPut<UpdateStrategyResponse>(`/api/v1/strategies/${strategyId}`, data);
    },
    makeUpdateStrategyStatusDemoFn(strategyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['strategies'] });
        queryClient.invalidateQueries({ queryKey: ['strategy', strategyId] });
      },
    },
  );
}

export function useToggleStrategyStatus() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateStrategyResponse, Error, { id: number | string; status: 'Active' | 'Inactive' }>(
    async (data) => {
      return apiPut<UpdateStrategyResponse>(`/api/v1/strategies/${data.id}/status`, { status: data.status });
    },
    toggleStrategyStatusDemoResponse,
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['strategies'] });
        queryClient.invalidateQueries({ queryKey: ['strategies-graphql-list'] });
        queryClient.invalidateQueries({ queryKey: ['strategy', variables.id] });
      },
    },
  );
}

export interface StrategyTypeOption {
  id: number;
  code: string;
  name: string;
}

export interface ActionTypeOption {
  id: number;
  code: string;
  name: string;
}

export function useStrategyTypeOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<StrategyTypeOption[], Error>(
    ['strategy-type-options'],
    async ({ signal }) => {
      const response = await apiGet<StrategyTypeLookupApi[]>('/api/v1/strategies/types', undefined, signal);
      return response.map((t) => ({ id: t.id, code: t.code, name: t.name }));
    },
    mockStrategyTypeOptions,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false },
  );
}

export function useActionTypeOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<ActionTypeOption[], Error>(
    ['action-type-options'],
    async ({ signal }) => {
      const response = await apiGet<StrategyActionTypeLookupApi[]>(
        '/api/v1/strategies/action-types',
        undefined,
        signal,
      );
      return response.map((t) => ({ id: t.id, code: t.code, name: t.name }));
    },
    mockActionTypeOptions,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false },
  );
}

export interface TemplateOption {
  id: number;
  name: string;
}

const EMAIL_TEMPLATE_TYPE_ID = 1;

export function useTemplatesByType(actionType: StrategyAction['type'] | undefined) {
  const { apiGet } = useApi();
  const isEmail = actionType === 'email';

  return useModeAwareQuery<TemplateOption[], Error>(
    ['templates-by-type', isEmail ? EMAIL_TEMPLATE_TYPE_ID : null],
    async ({ signal }) => {
      if (!isEmail) return [];
      const response = await apiGet<TemplateListItemApi[]>(
        `/api/v1/templates/type/${EMAIL_TEMPLATE_TYPE_ID}`,
        undefined,
        signal,
      );
      // A strategy is shared across the company group, so it stores the group-level id even when
      // this company sees its own override of that template.
      return response.map((t) => ({ id: t.logicalTemplateId ?? t.sourceTemplateId ?? t.id, name: t.name }));
    },
    isEmail ? mockEmailTemplateOptions : [],
    {
      enabled: isEmail,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}
